const IMAGE_MAX_DIM = 1920;
const IMAGE_JPEG_QUALITY = 0.82;
const IMAGE_SKIP_BELOW_BYTES = 350 * 1024;

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Image compression failed'));
      },
      'image/jpeg',
      quality
    );
  });
}

function withJpegExtension(name) {
  const base = String(name || 'image').replace(/\.[^.]+$/, '');
  return `${base || 'image'}.jpg`;
}

/**
 * Resize/compress post images in the browser before upload. Videos are optimized on the server
 * after upload (background H.264 transcode). Animated GIFs are left as-is.
 */
export async function optimizePostMediaFile(file) {
  if (!file) return file;
  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('video/')) return file;
  if (!/^image\/(jpeg|jpg|png|webp)$/i.test(type)) return file;
  if (type === 'image/gif') return file;

  try {
    const img = await loadImageElement(file);
    const maxDim = Math.max(img.naturalWidth || 1, img.naturalHeight || 1);
    const needsResize = maxDim > IMAGE_MAX_DIM;
    if (!needsResize && file.size <= IMAGE_SKIP_BELOW_BYTES) return file;
    const scale = Math.min(1, IMAGE_MAX_DIM / maxDim);
    const width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
    const height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToJpegBlob(canvas, IMAGE_JPEG_QUALITY);
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], withJpegExtension(file.name), { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
